import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { useLocale, useT } from '../i18n/index.js';
import { LandingIcon, WhatsAppGlyph } from '../ui/LandingIcon.js';
import { ShopifyGlyph } from '../ui/ShopifyGlyph.js';
import { FLAG_CODES, FLAG_LABELS, LandingFlag } from '../ui/LandingFlag.js';
import { BlogNav } from './BlogNav.js';
import { COMPANY, partnerContactHref } from '../legal/company.js';
import { ScratchCard } from './landing/ScratchCard.js';
import { Mascot3D } from './landing/Mascot3D.js';
import { LandingGuide } from './landing/LandingGuide.js';
import { useWelcomeOffer } from './landing/useWelcomeOffer.js';
import { OFFER } from './landing/offer.js';
import { money, planFigures, yearlyOffered, yearlySavings, type Period } from './landing/pricing.js';
import { ThemeCard } from './landing/ThemeCard.js';
import { THEME_LICENSE, isCurrency } from '@krevya/shared';
import { usePublicPricing } from './landing/usePublicPricing.js';
import { PipelineBand } from './landing/PipelineBand.js';
import { ChartsRow } from './landing/ChartsRow.js';
import { GalleryTrack } from './landing/GalleryTrack.js';
import { BLUR } from '../content/blur.js';
import { StickyOffer } from './landing/StickyOffer.js';
import { ConsentLink } from '../consent/ConsentBanner.js';
import { startScrollScenes } from './landing/scrollScenes.js';
import { useRenderNow } from '../lib/hydration.js';

/**
 * Every picture this page shows, in one place.
 *
 * The four product screenshots that used to be repeated through five sections
 * are gone: each section gets art chosen for that section, and it is dropped
 * in here rather than hunted for down the file. A slot left `null` renders an
 * empty stage that still holds its own space, so nothing on the page moves
 * when the picture finally arrives.
 *
 * Each slot carries the file's real pixel size next to it. The browser needs
 * both numbers before the bytes arrive to reserve the right box, and every one
 * of these pictures sits in a section further down a long page: without them
 * the whole page below the picture jumps as each one lands.
 *
 * The sources are WebP wherever a WebP of the same artwork exists. The PNGs
 * beside them are the same pictures at 60 to 100 times the weight: the sixteen
 * slots below were 11.5MB as PNG and are 172KB as WebP, on a page whose entire
 * job is to load fast enough to be read. `work-3` and `work-4` were the last
 * PNGs here, and their 1254px squares had to be decoded and uploaded to the
 * GPU in the middle of a scroll, which showed as a hitch; they are WebP now
 * too, at twice the size they are shown.
 */
type Picture = {
  src: string;
  w: number;
  h: number;
  /**
   * A 500px-wide copy and the width the picture is drawn at. A phone draws a
   * gallery card 250px wide, 500 real pixels on a 2x screen, and was sent the
   * 900px file; it now picks the copy, and a large screen still gets the full
   * file.
   */
  small?: { src: string; sizes: string };
};

const ART: Record<string, Picture | null> = {
  /*
   * The hero's three creatives, each cut from one of the finished ads in
   * `public/formats` to the ratio its label names, so the page shows the three
   * ratios it claims rather than printing them on an empty card.
   */
  'ratio-916': { src: '/landing/ratio-916.webp', w: 300, h: 533 },
  'ratio-11': { src: '/landing/ratio-11.webp', w: 360, h: 360 },
  'ratio-45': { src: '/landing/ratio-45.webp', w: 340, h: 425 },
  'ratio-11b': { src: '/landing/ratio-11b.webp', w: 340, h: 340 },
  demo: { src: '/landing/demo.webp', w: 1400, h: 876 },
  store: { src: '/landing/suite-3.webp', w: 1400, h: 788 },
  'step-1': { src: '/landing/step-1.webp', w: 854, h: 765 },
  'step-2': { src: '/landing/step-2.webp', w: 1000, h: 827 },
  'step-3': { src: '/landing/step-3.webp', w: 1000, h: 696 },
  'suite-1': { src: '/landing/suite-1.webp', w: 1400, h: 788 },
  'suite-2': { src: '/landing/suite-2.webp', w: 1400, h: 788 },
  'suite-3': { src: '/landing/suite-3.webp', w: 1400, h: 788 },
  'suite-4': { src: '/landing/suite-4.webp', w: 1400, h: 788 },
  'work-1': { src: '/landing/work-1.webp', w: 1000, h: 1000 },
  'work-2': { src: '/landing/work-2.webp', w: 1000, h: 939 },
  'work-3': { src: '/landing/work-3.webp', w: 1000, h: 1000 },
  'work-4': { src: '/landing/work-4.webp', w: 1000, h: 1000 },
};

/*
 * The four slots the suite carousel and the work grid are drawn from. Their
 * pictures are `suite-1..4` and `work-1..4`, and the tabs beside the carousel
 * count on there being exactly four.
 */
const SLOTS = [1, 2, 3, 4] as const;

/*
 * The finished ads: a headline, a layout and an offer set on the product, all
 * of them the demo brand's. This is the half of the claim a reader checks by
 * looking — whether the thing that comes out is an ad or a photograph with
 * hope attached.
 */
const GALLERY_ADS = [
  'formats/ugc-selfie',
  'formats/before-after',
  'formats/product-hero',
  'formats/benefit-stack',
  'formats/us-vs-them',
  'formats/unboxing-flatlay',
  'formats/how-it-works',
  'formats/material-closeup',
  'formats/seasonal-sale',
] as const;

/*
 * The scenes: one product per picture, across every category the page claims
 * to serve, each keeping the product exactly as it arrived. This is the other
 * half — that a store selling trainers or a jar of face cream is looking at
 * the same product.
 *
 * Ordered two people-led pictures, then one of anything else: a row of objects
 * reads as a catalogue, and the scenes with a person in them are the ones that
 * stop a thumb. The first four are what the prerendered HTML carries.
 */
const GALLERY_SCENES = [
  'showcase/stone-jetty',
  'showcase/cafe-amalfi',
  'showcase/ferry-deck',
  'showcase/gown',
  'showcase/poolside',
  'showcase/tea-garden',
  'showcase/y2k-flash',
  'showcase/seaside-rocks',
  'showcase/bread-olives',
  'showcase/sky-leap',
  'showcase/coat',
  'showcase/barber-counter',
  'showcase/window-light',
  'showcase/city-rooftop',
  'showcase/dog-living-room',
  'showcase/night-crosswalk',
  'showcase/handbag',
  'showcase/peach-juice',
  'showcase/winter-coat',
  'showcase/scarf-wind',
  'showcase/orange-chair',
  'showcase/espresso-ritual',
  'showcase/stone-vanity',
  'showcase/road-trip',
  'showcase/gym-reset',
  'showcase/catalogue-pose',
  'showcase/flower-market',
  'showcase/tulip-garden',
  'showcase/arched-niche',
  'showcase/glass-caustics',
  'showcase/living-room-read',
  'showcase/linen-line',
  'showcase/croissant-dip',
  'showcase/rainy-window',
  'showcase/gloved-hands',
  'showcase/tennis-court',
  'showcase/greenhouse-bench',
  'showcase/leather-car-seat',
  'showcase/shoji-light',
  'showcase/footwear',
  'showcase/fragrance',
  'showcase/beverage',
  'showcase/jewellery',
  'showcase/audio',
  'showcase/skincare-lab',
  'showcase/giant-handbag',
  'showcase/desert-fragrance',
  'showcase/sneaker-impact',
  'showcase/chrome-beauty',
  'showcase/cafe-handbag',
  'showcase/gold-bracelet',
  'showcase/espresso-machine',
  'showcase/headphones-neon',
  'showcase/flatlay-accessories',
  'showcase/ringed-hand',
  'showcase/denim-pocket',
  'showcase/campfire-lake',
  'showcase/laundry-room',
  'showcase/soap-bubbles',
] as const;
const POSES = ['read', 'search', 'idea', 'ok'] as const;

/**
 * Whether an element has come near the viewport yet, once and for good.
 *
 * The gallery turns more than fifty pictures and the row holds two copies of
 * them, so putting all of it in the prerendered HTML is a hundred-odd image
 * elements the browser parses and lays out before the page can be used, for a
 * row most readers scroll past and many never reach. Only the first few are
 * rendered until the row is a screen away; the rest join then, which is
 * still before anyone can see the difference.
 *
 * It never goes back to false: a row that emptied itself again on the way
 * past would be worse than the cost it saves.
 */
function useNearViewport(ref: RefObject<HTMLElement | null>): boolean {
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || near) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setNear(true);
        observer.disconnect();
      },
      // A screen of warning: the row is still off screen when it fills, and a
      // visitor who never scrolls past the hero never pays for it at all.
      { rootMargin: '100% 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, near]);
  return near;
}

/** One picture slot: the art if it has been chosen, its empty stage if not. */
function Art({ slot, className = '' }: { slot: string; className?: string }) {
  // A gallery slot names its own folder: `formats/before-after`,
  // `showcase/coat`. Everything else is a picture from the map above.
  const key = slot.startsWith('gallery:') ? slot.slice(8) : null;
  const art = key ? { src: `/${key}.webp`, w: 640, h: 800 } : ART[slot];
  if (!art) return <div className={`lp-art-empty ${className}`} aria-hidden="true" />;
  /*
   * The sixteen-pixel copy, behind the real one. It is the picture's own
   * colours in the picture's own places, so a card that has not loaded yet is
   * a soft version of what is coming rather than a white hole sliding past;
   * the real pixels paint over it when they arrive, with no state to keep and
   * nothing to fade.
   */
  const blur = key ? BLUR[key] : undefined;
  return (
    <img
      className={className}
      {...(blur
        ? {
            style: {
              backgroundImage: `url(data:image/webp;base64,${blur})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            },
          }
        : {})}
      src={art.src}
      {...(art.small
        ? { srcSet: `${art.small.src} 500w, ${art.src} ${art.w}w`, sizes: art.small.sizes }
        : {})}
      alt=""
      width={art.w}
      height={art.h}
      loading="lazy"
      decoding="async"
    />
  );
}

/**
 * One of Magix's poses, decorative everywhere it appears.
 *
 * Every pose is a 384x384 file. The numbers are written once here rather than
 * on ten separate tags, and they matter: these sit inside sections that are
 * laid out around them, so a mascot that arrives without a reserved box drags
 * the copy beside it sideways as it lands.
 */
function Mascot({ pose, className = '' }: { pose: string; className?: string }) {
  return (
    <img
      className={className}
      src={`/magix/${pose}.webp`}
      alt=""
      aria-hidden="true"
      width={384}
      height={384}
      loading="lazy"
      decoding="async"
    />
  );
}

/**
 * The manifesto's three lines, a word to a span.
 *
 * Each word carries the point of the scroll at which it lights (`--w`, 0 to
 * 1); the CSS compares it with the section's progress. The words are spread
 * over the first 85% of the run so the last one is lit while the section is
 * still held, not as it leaves.
 */
function ManifestoLines({ lines }: { lines: string[] }) {
  const words = lines.map((line) => line.split(' '));
  const total = words.reduce((n, line) => n + line.length, 0);
  let at = 0;
  return (
    <>
      {words.map((line, i) => (
        <p key={lines[i]}>
          {line.map((word, j) => {
            const w = (at++ / total) * 0.85;
            return (
              <span className="lp-sweep-word" style={{ '--w': w.toFixed(3) } as CSSProperties} key={j}>
                {word}
                {j < line.length - 1 ? ' ' : null}
              </span>
            );
          })}
          <Mascot pose={POSES[i]!} />
        </p>
      ))}
    </>
  );
}

type Pair = [string, string];

function Button({
  children,
  href = '/register',
  quiet = false,
}: {
  children: ReactNode;
  href?: string;
  quiet?: boolean;
}) {
  return (
    <a className={`lp-button${quiet ? ' lp-button-quiet' : ''}`} href={href}>
      {children}
      <LandingIcon name="arrow" />
    </a>
  );
}

function Frame({ slot, className = '' }: { slot: string; className?: string }) {
  return (
    <div className={`lp-frame ${className}`}>
      <div className="lp-frame-bar">
        <i />
        <i />
        <i />
      </div>
      <Art slot={slot} />
    </div>
  );
}

const THEME_PERK = 'Krevya Theme, free';

export function LandingPage() {
  const { locale, formatLocale } = useLocale();
  const t = useT();
  const tr = locale === 'tr';
  const tx = (en: string, turkish: string) => (tr ? turkish : en);
  const { plans, currency, settled: pricesSettled } = usePublicPricing(locale);
  // The one-time theme licence's price in the visitor's currency, shown struck
  // wherever the theme is said to be free with a plan.
  const themePrice = money(
    THEME_LICENSE.prices[isCurrency(currency) ? currency : 'usd'],
    currency,
    formatLocale,
  );
  const yearly = yearlyOffered(plans);
  const savings = yearlySavings(plans);
  const [period, setPeriod] = useState<Period>('month');
  // The switch is hidden when a plan has no yearly price, so a stale 'year'
  // must not leave the cards showing a period nobody can switch back from.
  const shown: Period = yearly ? period : 'month';
  const planCopy: Record<string, [string, string]> = {
    starter: ['For stores getting started', 'Yeni başlayan mağazalar için'],
    growth: ['For growing stores', 'Büyüyen mağazalar için'],
    pro: ['For high-volume production', 'Yoğun üretim yapanlar için'],
    partner: ['For agencies and high-volume brands', 'Ajanslar ve yüksek hacimli markalar için'],
  };
  // What Partner adds beyond the rows every card shares. The credits and the
  // stores are already rows; these are the things only an agreement gives.
  /*
   * What a plan carries besides its figures. The Krevya Theme comes free with
   * every plan, which a reader comparing plans should see on the card rather
   * than only further down; Growth and Pro add live support from a person,
   * and Partner its agreed extras.
   */
  const planPerks = (planId: string, agreed: boolean): [string, string][] => [
    [THEME_PERK, 'Krevya Theme ücretsiz'],
    ...(planId === 'growth' || planId === 'pro'
      ? ([['Dedicated live support', 'Sana özel canlı destek']] as [string, string][])
      : []),
    ...(agreed ? partnerExtras : []),
  ];
  const partnerExtras: [string, string][] = [
    ['Unlimited ad accounts', 'Sınırsız reklam hesabı'],
    ['Custom API access', 'Özel API erişimi'],
    ['1-on-1 contact support', '1-1 iletişim desteği'],
    ['Custom theme design', 'Özel tema tasarımı'],
  ];
  // Counted from `theme/`; see the comment on the theme card before changing a number.
  const pricingTools: [string, string][] = [
    ['Competitor research', 'Rakip araştırması'],
    ['Ad creatives', 'Reklam kreatifleri'],
    ['Store pages', 'Mağaza sayfaları'],
    ['Brand kits', 'Marka kitleri'],
    ['Library', 'Kütüphane'],
    ['Email flows and signups', 'E-posta akışları ve kayıtlar'],
  ];
  /*
   * How far the carousel has turned, counted in quarter turns and never
   * wrapped. The screen in front is the remainder. Wrapping the angle itself
   * sent the ring from -270deg back to 0 on every fourth step, which is three
   * quarters of a turn backwards in the time one step forwards takes.
   */
  const gallery = useRef<HTMLElement>(null);
  const galleryNear = useNearViewport(gallery);
  /* Four a row is more than fits on the widest screen, so nobody sees a short one. */
  const galleryAds = galleryNear ? GALLERY_ADS : GALLERY_ADS.slice(0, 4);
  const galleryScenes = galleryNear ? GALLERY_SCENES : GALLERY_SCENES.slice(0, 4);

  const [turn, setTurn] = useState(0);
  const suite = ((turn % SLOTS.length) + SLOTS.length) % SLOTS.length;
  const [suitePaused, setSuitePaused] = useState(false);
  /*
   * Every way the reader turns the carousel (a tab, an arrow key, a swipe, a
   * drag) stops its walk, so the screen they picked is the one that stays.
   * Going to a screen takes the short way round: one step back rather than
   * three forward.
   */
  const showSuite = (i: number) => {
    setTurn((value) => {
      const at = ((value % SLOTS.length) + SLOTS.length) % SLOTS.length;
      const ahead = (i - at + SLOTS.length) % SLOTS.length;
      return value + (ahead === SLOTS.length - 1 ? -1 : ahead);
    });
    setSuitePaused(true);
  };
  const stepSuite = (step: 1 | -1) => {
    setTurn((value) => value + step);
    setSuitePaused(true);
  };
  /*
   * Where a finger or the mouse went down on the ring. The gesture only
   * becomes a turn once it has gone 30px, and further across than down:
   * before that it may still be the reader scrolling the page, which
   * `touch-action: pan-y` leaves to the browser. One gesture is one step,
   * however far it travels, the way one tap on a tab is.
   */
  const ringPointer = useRef<{ id: number; x: number; y: number } | null>(null);
  /*
   * The welcome offer. `offered` is false once the offer's end date has passed,
   * which is what takes both the card and the pill off the page rather than
   * leaving a discount nobody can spend sitting beside the hero button.
   */
  const offer = useWelcomeOffer();

  const steps: Pair[] = tr
    ? [
        [
          'Shopify mağazanı bağla',
          'Shopify’dan ürünlerini, fiyatlarını ve görsellerini içeri aktar; istersen ürünü elle de ekle.',
        ],
        [
          'Satış açısını araştır',
          'Krevya ürünü ve rakip sayfaları okuyup faydaları, itirazları ve rakiplerin boş bıraktığı mesajları çıkarır.',
        ],
        [
          'Neyi test edeceğini seç',
          'Bir satış açısını seç, geç veya tek cümleyle yön ver. Ürünle ilgili gerekli bağlam fikirle birlikte kalır.',
        ],
        [
          'Üret ve yayına hazırla',
          'Kreatifini 1:1, 4:5 veya 9:16 üret; aynı satış açısını Shopify ürün sayfana da taşı.',
        ],
      ]
    : [
        [
          'Connect your store',
          'Import products, prices and images from Shopify, or add a product manually.',
        ],
        [
          'Research the angle',
          'Krevya reads the product and competitor pages to surface benefits, objections and messaging gaps.',
        ],
        [
          'Choose what to test',
          'Pick an angle, pass on it, or steer it in one sentence. The useful product context stays attached.',
        ],
        [
          'Create and ship',
          'Generate the creative in 1:1, 4:5 or 9:16, then build the Shopify page around the same angle.',
        ],
      ];
  const dna: Pair[] = tr
    ? [
        [
          'Ürünü gerçekten bilir',
          'Ürün adı, fiyatı, görselleri ve doğrulanabilir özellikleri katalogdan gelir.',
        ],
        [
          'Satın alma açısını bulur',
          'Ürün ve rakip araştırmasını fayda, itiraz ve test edilebilir mesaja çevirir.',
        ],
        ['Markanı bozmaz', 'Ton, görsel yön ve kesin marka kuralları her üretime taşınır.'],
        [
          'Seçimlerinden öğrenir',
          'Tuttuğun ve geçtiğin fikirler, sonraki önerilerde hangi yönlerin öne çıkacağını etkiler.',
        ],
      ]
    : [
        [
          'Knows the product',
          'Names, prices, images, claims and product details stay grounded in your catalogue.',
        ],
        [
          'Finds the buying angle',
          'Product and competitor research becomes benefits, objections and testable messages.',
        ],
        ['Stays on-brand', 'Tone, visual direction and hard rules carry across every generation.'],
        [
          'Learns what you keep',
          'Your approvals and skips help prioritise better-fit angles next time.',
        ],
      ];
  const signals: Pair[] = tr
    ? [
        ['Araştırmaya dayalı açılar', 'Fikrin arkasındaki ürün veya pazar sinyalini gör.'],
        [
          'Farklı hipotezler',
          'Aynı mesajın on görselini değil, satın almak için farklı nedenleri test et.',
        ],
        [
          'Ürün doğruluğu kontrolü',
          'Ürün detayları ve reklam metni teslim edilmeden önce kontrol edilir.',
        ],
        [
          'Geri bildirim ileri taşınır',
          'Tuttuğun ve geçtiğin yönler, sonraki önerilerin sırasını etkiler.',
        ],
      ]
    : [
        ['Research-backed angles', 'See the product or market signal behind the idea.'],
        [
          'Different hypotheses',
          'Test different reasons to buy, not ten visual versions of the same message.',
        ],
        [
          'Product fidelity checks',
          'Product details and copy are checked before the work is delivered.',
        ],
        [
          'Feedback carries forward',
          'Keep and pass decisions influence which directions surface next.',
        ],
      ];
  const faqs: Pair[] = tr
    ? [
        [
          'Krevya tam olarak nedir?',
          'Krevya; mağaza verini ve araştırmayı reklam kreatifine ve Shopify ürün sayfasına dönüştüren, ürünü tanıyan bir e-ticaret çalışma alanıdır.',
        ],
        [
          'Genel yapay zekâ araçlarından farkı ne?',
          'Genel yapay zekâ yalnızca yazdığın promptla başlar. Krevya kataloğunu, marka kurallarını, araştırmayı, geçmiş üretimleri ve geri bildirimini aynı bağlamda tutar; her seferinde baştan anlatmazsın.',
        ],
        [
          'Shopify şart mı?',
          'Shopify en hızlı bağlantı ama şart değil. Bir ürünü adı, açıklaması ve görselleriyle elle ekleyerek de çalışabilirsin.',
        ],
        [
          'Tasarım bilmem gerekiyor mu?',
          'Hayır. Bir satış açısı seç, üret ve değiştirmek istediğini günlük konuşma diliyle yaz.',
        ],
        [
          'Kredi nedir, neye yeter?',
          'Kredi, planların satıldığı birim. Standart bir görsel 10 kredi; Growth’un aylık 1.200 kredisi yaklaşık 120 görsel eder. Araştırma da kredi harcar ve her işlemin kaç kredi tutacağı başlatmadan önce ekranda görünür.',
        ],
        [
          'Kullanmadığım krediler ne olur?',
          'Kullanmadığın plan kredileri aboneliğin sürdükçe devreder. Plan bittiğinde kullanılmamış plan kredileri de biter. Krevya’nın hesabına eklediği krediler sende kalır.',
        ],
        [
          'Üretim yarıda kalırsa kredi gider mi?',
          'Hayır. Kredi iş başlarken ayrılır, yalnızca teslim edilen iş için harcanır. Başarısız bir üretim için ayrılan krediler geldiği yere geri döner.',
        ],
        [
          'Planımı sonradan değiştirebilir miyim?',
          'Evet. Yeni planı Krevya’nın içinden seçersin, bugün başlar. Kart değiştirme ve iptal, fatura sayfandan açılan Whop ödeme sayfasında yapılır. Krevya kartını hiçbir zaman görmez.',
        ],
        [
          'Yıllık plan nasıl işliyor?',
          'Yıllık ödeme yılda bir kez yapılır, yaklaşık %20 daha ucuzdur. Kredilerin yine her ay yüklenir: Growth her ay 1.200, yıl boyunca toplam 14.400 kredi.',
        ],
        [
          'İptal nasıl işliyor?',
          'İptal bir sonraki yenilemeyi durdurur; plan ve yıllık planda aylık kredi yüklemeleri ödediğin dönemin sonuna kadar sürer. Sonra kullanılmamış plan kredileri biter; Krevya’nın eklediği krediler sende kalır. Yıllık ödemelerde iade yok.',
        ],
        [
          'Mağaza sayfası Shopify’a nasıl gidiyor?',
          'Krevya, sayfayı Krevya Theme’e kurar ve temayı mağazana yayınlanmamış bir taslak olarak ekler. Önce önizlersin, beğenirsen yayınlarsın; neyin yayına çıkacağına sen karar verirsin. Krevya Theme her planda ücretsiz.',
        ],
      ]
    : [
        [
          'What exactly is Krevya?',
          'Krevya is an AI commerce workspace that turns store data and research into ad creative and Shopify product pages, while keeping the product context across the workflow.',
        ],
        [
          'How is it different from general AI tools?',
          'General AI starts with what you type. Krevya starts with your catalogue, brand rules, research, past work and feedback, so you do not rebuild context every session.',
        ],
        [
          'Do I need Shopify?',
          'Shopify is the fastest connection, but it is not required. You can also add a product manually with its name, description and images.',
        ],
        [
          'Do I need design skills?',
          'No. Choose an angle, generate the work, and ask for changes in plain language.',
        ],
        [
          'What is a credit, and what does it buy?',
          'A credit is the unit every plan is sold in. A standard image costs 10 credits, so Growth’s 1,200 credits a month are about 120 images. Research runs cost credits too, and what any action costs is shown on screen before you start it.',
        ],
        [
          'What happens to credits I don’t use?',
          'Unused plan credits roll over for as long as your subscription runs. When the plan ends, its unused plan credits end with it. Credits Krevya adds to your account stay yours.',
        ],
        [
          'Do failed generations use credits?',
          'No. Credits are reserved when a job starts and spent only on what is delivered. Credits held for a generation that fails go back to where they came from.',
        ],
        [
          'Can I change my plan later?',
          'Yes. Choose a new plan from inside Krevya and it starts today. Changing your card and cancelling happen on Whop’s billing page, opened from your billing page. Krevya never sees your card.',
        ],
        [
          'How does yearly work?',
          'Yearly is paid once a year, about 20% less. Your credits still arrive every month: Growth adds 1,200 each month, 14,400 over the year.',
        ],
        [
          'How does cancelling work?',
          'Cancelling stops the next renewal; the plan, and its monthly credits on a yearly plan, keep running until the end of what you paid for. Then unused plan credits end; credits Krevya adds stay. Yearly payments are not refunded.',
        ],
        [
          'How does a store page reach my Shopify store?',
          'Krevya builds the page into the Krevya Theme and adds that theme to your store as an unpublished draft. Preview it, and publish it when you are happy: you decide what goes live. The Krevya Theme is free on every plan.',
        ],
      ];
  const ideas = tr
    ? [
        'Problem → çözüm',
        'Ürün kanıtı',
        'Müşteri yorumu',
        'Fayda listesi',
        'UGC',
        'Kampanya',
        'Önce ve sonra',
        'Karşılaştırma',
        'Öne çıkan rakam',
        'Soru / cevap',
      ]
    : [
        'Problem → solution',
        'Product proof',
        'Review',
        'Benefit stack',
        'UGC',
        'Offer',
        'Before & after',
        'Comparison',
        'Key number',
        'Q&A',
      ];
  const suiteTabs = tr
    ? ['Ürünler', 'Araştırma', 'Kreatif', 'Mağaza sayfaları']
    : ['Products', 'Research', 'Creative', 'Store pages'];
  const comparisonRows = tr
    ? [
        'Kataloğunu tanır',
        'Marka kurallarını işe taşır',
        'Satış açılarını araştırmadan çıkarır',
        'Görseli ve metni birlikte üretir',
        'Onayladığın yönleri hatırlar',
      ]
    : [
        'Knows your catalogue',
        'Carries brand rules across the work',
        'Builds angles from research',
        'Creates image and copy together',
        'Remembers what you approve',
      ];
  /*
   * Named tools rather than categories.
   *
   * "Prompt box" and "template tool" describe nothing a visitor recognises;
   * the three products people are actually deciding between are ChatGPT,
   * Canva and Midjourney, and their marks are read faster than any label.
   * Claims are limited to what each product does not attempt: none of them
   * connects to a catalogue, and none carries brand rules between sessions.
   */
  const comparisons = useMemo(
    () => [
      {
        label: 'Krevya',
        logo: '/brand/krevya-wordmark.webp',
        values: [true, true, true, true, true],
      },
      { label: 'ChatGPT', logo: '/brand/openai.svg', values: [false, false, false, true, false] },
      { label: 'Canva', logo: '/brand/canva.svg', values: [false, true, false, false, false] },
      {
        label: 'Midjourney',
        logo: '/brand/midjourney.svg',
        values: [false, false, false, false, false],
      },
    ],
    [locale],
  );

  useEffect(() => {
    const root = document.querySelector<HTMLElement>('.lp');
    if (!root) return;
    /*
     * A section is revealed once, as soon as any of it is 80px inside the
     * screen: no ratio threshold, so a tall section or one already partly in
     * view on a 1080p monitor still plays, and no hiding again on the way
     * back up, which only made the page flicker. The values are the
     * reference site's the owner asked this page to move like.
     */
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          (entry.target as HTMLElement).dataset.in = 'true';
          observer.unobserve(entry.target);
        }),
      { threshold: 0, rootMargin: '-80px 0px -80px 0px' },
    );
    root.querySelectorAll<HTMLElement>('[data-reveal]').forEach((el) => observer.observe(el));
    // The hero's mascot drifts up with the scroll; the scenes' frame writes
    // its value along with theirs, after every box has been read.
    const stopScenes = startScrollScenes(
      root,
      Array.from(root.querySelectorAll<HTMLElement>('.lp-hero-magix')),
    );
    return () => {
      observer.disconnect();
      stopScenes();
    };
  }, []);

  /*
   * The carousel walks itself until the reader takes it over, and then it
   * stays where they put it. Four and a bit seconds is long enough to read
   * one screen, and the turn takes 700ms of that. It turns with reduced motion
   * on too: the owner decided the page moves the same for everybody.
   */
  useEffect(() => {
    if (suitePaused) return;
    const timer = window.setInterval(() => setTurn((value) => value + 1), 4200);
    return () => window.clearInterval(timer);
  }, [suitePaused]);

  return (
    /*
     * `data-motion` is rendered, not set by an effect after the first paint:
     * a section the browser skips until it nears the screen keeps the style
     * it was first given, and given the visible one first it faded out and
     * back in when it was reached instead of rising once.
     */
    <div className="lp" data-motion="ready">
      {/* The blog's bar, which the owner preferred to this page's own. */}
      <div className="blog-chrome">
        <BlogNav here="home" />
      </div>
      <main>
        <section className="lp-hero">
          {/* No badge over the headline: the owner wants no eyebrows on this page. */}
          <h1>
            <span>{tx('Stop starting', 'Promptla uğraşma.')}</span>
            <strong>{tx('from a blank prompt.', 'Ürünü tanıyan kreatif üret.')}</strong>
          </h1>
          <p className="lp-hero-copy">
            {tx(
              'Connect Shopify. Krevya researches the product, finds angles worth testing, and turns them into ad creative and Shopify product pages.',
              'Shopify mağazanı bağla. Krevya ürünü ve rakipleri araştırır, test edilecek satış açılarını çıkarır; bunları reklam kreatifine ve Shopify ürün sayfasına dönüştürür.',
            )}
          </p>
          <div className="lp-actions">
            <Button>{t('landing.hero.cta')}</Button>
            <Button href="#how" quiet>
              {t('landing.hero.secondary')}
            </Button>
          </div>
          {/*
            The offer, kept beside the button it applies to.
            It is a button rather than a label because the card it came from can
            be reopened from it: somebody who closed the card without reading it
            has no other way back to the code.
          */}
          {offer.offered ? (
            <button type="button" className="lp-offer-pill" onClick={offer.reopen}>
              <LandingIcon name="tag" />
              {t('landing.offer.pill', { percent: String(OFFER.percent) })}
            </button>
          ) : null}
          <div className="lp-proof">
            <span>
              <LandingIcon name="check" /> {tx('No card required', 'Kredi kartı gerekmez')}
            </span>
            <span>
              <LandingIcon name="spark" />{' '}
              {tx('Catalogue imported in minutes', 'Kataloğun dakikalar içinde hazır')}
            </span>
            {/*
              The third proof used to be "200+ active customers" over three
              mascot portraits. Krevya does not publish a customer count, and a
              number nobody can check is the one claim on this page that would
              make a reader doubt the ones that are true. What it makes instead
              is the promise the rest of the page repeats: Store Pages does send
              a page to Shopify, but only as a draft, so the merchant is still
              the one who decides what a shopper ever sees.
            */}
            <span>
              <LandingIcon name="shield" />{' '}
              {tx('Nothing goes live without you', 'Sen onaylamadan hiçbir şey yayına çıkmaz')}
            </span>
          </div>
          <p className="lp-works">
            <span>{tx('Works with', 'Şununla çalışır:')}</span>
            <ShopifyGlyph label="Shopify" />
          </p>
          <div className="lp-hero-stage" aria-hidden="true">
            <div className="lp-orbit lp-orbit-a" />
            <div className="lp-orbit lp-orbit-b" />
            {/*
              The one picture above the fold, so it is the one picture that is
              not lazy: it is this page's largest paint, and deferring it means
              the hero is a headline over an empty stage for the first second.
            */}
            <Mascot3D className="lp-hero-magix" priority />
            {/*
              Three creatives Krevya made, one in each ratio it ships, standing
              where two cards used to print the ratios over nothing. The ratio
              is still named, but now it labels something: the reader sees what
              a story and a feed ad actually come out as before reading a word
              of the argument for them.
            */}
            <figure className="lp-shot lp-shot-story">
              <Art slot="ratio-916" />
              <figcaption>9:16</figcaption>
            </figure>
            <figure className="lp-shot lp-shot-feed">
              <Art slot="ratio-11" />
              <figcaption>1:1</figcaption>
            </figure>
            <figure className="lp-shot lp-shot-tall">
              <Art slot="ratio-45" />
              <figcaption>4:5</figcaption>
            </figure>
            <figure className="lp-shot lp-shot-macro">
              <Art slot="ratio-11b" />
              <figcaption>1:1</figcaption>
            </figure>
          </div>
        </section>

        {/* The claim the hero makes, said once in one line, with the four stages it rests on. */}
        <PipelineBand tx={tx} />

        {/*
          Two rows, each with the thing it is.
          One row of everything read as a screensaver: fifty-nine pictures
          sliding past with nothing said about any of them, which is the
          opposite of a page arguing that each one was reasoned about. Split,
          each row answers a different question — what a finished ad looks
          like, and how far the same product travels — and the count says how
          many there are to find.
        */}
        <section className="lp-gallery" data-reveal ref={gallery}>
          <h2>
            {tx(
              'One catalogue. Many angles worth testing.',
              'Tek katalogdan, test edilecek farklı satış açıları.',
            )}
          </h2>

          <p className="lp-gallery-label">
            <b>{tx('The finished ad', 'Bitmiş reklam')}</b>
            {tx(
              'Headline, layout and offer, laid out on the product.',
              'Başlık, düzen ve teklif; ürünün üstüne yerleşmiş.',
            )}
          </p>
          <GalleryTrack copies={2}>
            {[...galleryAds, ...galleryAds].map((slot, i) => (
              <div className="lp-gallery-card" key={`${slot}-${i}`}>
                <Art slot={`gallery:${slot}`} />
              </div>
            ))}
          </GalleryTrack>

          <p className="lp-gallery-label">
            <b>{tx('The same product, any world', 'Aynı ürün, her dünyada')}</b>
            {tx(
              `${GALLERY_SCENES.length} scenes, each keeping the product exactly as it is.`,
              `${GALLERY_SCENES.length} sahne; her birinde ürün olduğu gibi kalıyor.`,
            )}
          </p>
          <GalleryTrack copies={2}>
            {[...galleryScenes, ...galleryScenes].map((slot, i) => (
              <div className="lp-gallery-card" key={`${slot}-${i}`}>
                <Art slot={`gallery:${slot}`} />
              </div>
            ))}
          </GalleryTrack>
        </section>

        {/* Where a store watches for what changes: four illustrative charts, labelled as such. */}
        <ChartsRow tx={tx} />

        {/*
         * The second product, directly after the first.
         *
         * The gallery above is the page's one concrete "here is what Krevya
         * makes", and until now everything that followed it was about ad
         * images. The store page is half the product and was being sold like a
         * footnote, so it is introduced in the same breath as the ads rather
         * than after fifteen sections of argument: what the ad is, then what
         * the ad opens. It borrows the page's existing parts — the centred
         * header, the browser frame, the icon-and-line list — so it reads as a
         * section that was always here.
         */}
        {/* `#store` is the anchor the blog's own nav has been pointing at. */}
        <section className="lp-store" id="store" data-reveal>
          <header>
            <h2>
              {tx(
                'Make the product page continue the ad.',
                'Reklam ne söylüyorsa, ürün sayfası onu devam ettirsin.',
              )}
            </h2>
            <p>
              {tx(
                'Turn the same product research and creative angle into a full Shopify product page. Krevya writes its 12+ sections, creates the visuals, and sends it to your store as a draft.',
                'Seçtiğin reklam açısını aynı ürün araştırmasıyla 12+ bölümlük tam bir Shopify ürün sayfasına dönüştürür; metni ve görselleri hazırlar, mağazana taslak olarak yollar.',
              )}
            </p>
          </header>
          <Frame slot="store" className="lp-store-frame" />
          <ul className="lp-store-points lp-stagger">
            {(
              [
                [
                  'palette',
                  tx('On the Krevya Theme, free with your plan', 'Krevya Theme’de, aboneliğinle ücretsiz'),
                ],
                [
                  'spark',
                  tx(
                    'Copy and visuals from product context',
                    'Metin ve görseller ürün bağlamından',
                  ),
                ],
                [
                  'shield',
                  tx('Nothing goes live without you', 'Sen onaylamadan hiçbir şey yayına çıkmaz'),
                ],
              ] as const
            ).map(([icon, line]) => (
              <li key={line}>
                <LandingIcon name={icon} />
                {line}
              </li>
            ))}
          </ul>
        </section>

        {/*
          The statement holds still while it is read. The section runs a
          short way past its text and the text sticks for that distance, so
          the reader scrolls through it in place, and each word lights up as
          the scroll reaches it: the sentence is handed over at the pace it is
          read rather than arriving as a block. The run starts as the text
          settles and ends as it lets go; `scroll.css` has the arithmetic. A
          phone shows the statement still and whole.
        */}
        <section className="lp-manifesto" data-scroll="start 0.25 / end 0.7">
          <div className="lp-manifesto-pin">
            <ManifestoLines
              lines={[
                tx(
                  'Krevya starts with the product, not the prompt.',
                  'Krevya işe prompttan değil, üründen başlar.',
                ),
                tx(
                  'It combines your catalogue, brand rules and market research to understand what customers care about.',
                  'Kataloğunu, marka kurallarını ve pazar araştırmasını birlikte okuyup müşterinin neye önem verdiğini çıkarır.',
                ),
                tx(
                  'Every angle, visual and page starts from the same product context, so you do not rebuild it in every tool.',
                  'Böylece her reklam açısı, görsel ve ürün sayfası aynı ürün bilgisinden beslenir; her araçta baştan anlatmazsın.',
                ),
              ]}
            />
          </div>
        </section>

        <section className="lp-demo" data-reveal>
          <Mascot className="lp-demo-magix" pose="work" />
          <Frame slot="demo" />
        </section>

        <section className="lp-how" id="how">
          <h2 data-reveal>
            {tx(
              'From catalogue to campaign, in four steps.',
              'Katalogdan kampanyaya, dört adımda.',
            )}
          </h2>
          {/*
            A stack: each step sticks under the bar and the next slides up
            over it, the ones underneath shrinking back a step at a time as
            the reader moves through the four.
          */}
          <div className="lp-steps" data-stack data-scroll="start start / end end">
            {steps.map(([title, body], i) => (
              <article
                className="lp-step"
                data-reveal
                style={
                  {
                    '--i': i,
                    '--n': steps.length,
                    // How far this card shrinks by the end: 5% for each card
                    // that lands on top of it, never past 15%.
                    '--shrink': Math.min(0.15, 0.05 * (steps.length - 1 - i)),
                  } as CSSProperties
                }
                key={title}
              >
                <div className="lp-step-line">
                  <span>{String(i + 1).padStart(2, '0')}</span>
                </div>
                <div className="lp-step-copy">
                  <h3>{title}</h3>
                  <p>{body}</p>
                </div>
                <div className={`lp-step-visual lp-step-visual-${i + 1}`}>
                  <Mascot pose={POSES[i]!} />
                  {i < 3 ? (
                    <Frame slot={`step-${i + 1}`} />
                  ) : (
                    <div className="lp-formats">
                      <b>1:1</b>
                      <b>4:5</b>
                      <b>9:16</b>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="lp-suite" data-reveal>
          <header>
            <h2>
              {tx(
                'One product context across the whole workflow.',
                'Ürün bilgisi bütün akış boyunca aynı yerde.',
              )}
            </h2>
            <p>
              {tx(
                'Research, creative, store pages and your library stay connected, so you stop re-explaining the product in every tool.',
                'Araştırma, kreatif, mağaza sayfası ve arşiv birbirinden kopmaz; aynı ürünü her araçta yeniden anlatmazsın.',
              )}
            </p>
          </header>
          <div
            className="lp-ring"
            aria-hidden="true"
            onPointerDown={(event) => {
              if (event.pointerType === 'mouse' && event.button !== 0) return;
              ringPointer.current = { id: event.pointerId, x: event.clientX, y: event.clientY };
              // A mouse that leaves the ring mid-drag should still finish the
              // turn; a touch is already held by the element it started on.
              if (event.pointerType === 'mouse') {
                event.currentTarget.setPointerCapture(event.pointerId);
              }
            }}
            onPointerMove={(event) => {
              const start = ringPointer.current;
              if (!start || start.id !== event.pointerId) return;
              const dx = event.clientX - start.x;
              const dy = event.clientY - start.y;
              if (Math.abs(dx) <= Math.abs(dy)) {
                // Mostly up or down: the page's scroll, not the ring's.
                if (Math.abs(dy) >= 30) ringPointer.current = null;
                return;
              }
              if (Math.abs(dx) < 30) return;
              ringPointer.current = null;
              // Pulled left, the screen on the right comes round to the front.
              stepSuite(dx < 0 ? 1 : -1);
            }}
            onPointerUp={() => {
              ringPointer.current = null;
            }}
            onPointerCancel={() => {
              ringPointer.current = null;
            }}
            onDragStart={(event) => event.preventDefault()}
          >
            <div className="lp-ring-turn" style={{ '--turn': turn } as CSSProperties}>
              {SLOTS.map((slot, i) => (
                <div
                  className={`lp-ring-card lp-ring-${i}`}
                  data-front={suite === i ? 'true' : 'false'}
                  key={slot}
                >
                  <Art slot={`suite-${slot}`} />
                </div>
              ))}
            </div>
          </div>
          {/*
           * Plain buttons rather than a tablist: the thing they turn is the
           * decorative carousel beside them, not a panel of content, and a
           * tab that controls no panel is a promise to a screen reader that
           * the page does not keep.
           */}
          <div className="lp-suite-tabs">
            {suiteTabs.map((label, i) => (
              <button
                type="button"
                aria-pressed={suite === i}
                onClick={() => showSuite(i)}
                onKeyDown={(event) => {
                  // Left and right walk the labels and the ring together, so
                  // focus stays on the label of the screen in front.
                  if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
                  event.preventDefault();
                  const next =
                    (i + (event.key === 'ArrowRight' ? 1 : -1) + SLOTS.length) % SLOTS.length;
                  showSuite(next);
                  event.currentTarget.parentElement
                    ?.querySelectorAll<HTMLButtonElement>('button')
                    .item(next)
                    ?.focus();
                }}
                key={label}
              >
                <span>{label}</span>
                <i>
                  <b />
                </i>
              </button>
            ))}
          </div>
        </section>

        <section className="lp-dna lp-sides" id="dna" data-reveal>
          <header>
            <h2>
              {tx(
                'Context that carries forward',
                'Genel yapay zekânın unuttuğu bağlamı Krevya tutar.',
              )}
            </h2>
            <p>
              {tx(
                'Product facts, brand rules, audience signals and feedback stay attached to the work instead of disappearing between tools.',
                'Ürün gerçekleri, marka kuralları, müşteri sinyalleri ve geri bildirim her üretimde aynı bağlama bağlı kalır.',
              )}
            </p>
          </header>
          <div className="lp-dna-grid lp-stagger">
            {dna.map(([title, body], i) => (
              <article key={title}>
                {/*
                  The icon leads the card. It sat under the paragraph, where a
                  40px tile alone at the bottom of a stretched card reads as
                  something that failed to load rather than as a mark for the
                  heading above it.
                */}
                <div className="lp-card-head">
                  <span className={`lp-dna-icon icon-${i}`}>
                    <LandingIcon name={(['palette', 'signal', 'target', 'shield'] as const)[i]!} />
                  </span>
                  <h3>{title}</h3>
                </div>
                <p>{body}</p>
              </article>
            ))}
            <Mascot pose="wave" />
          </div>
        </section>

        <section className="lp-types" data-reveal>
          <h2>
            {tx(
              'Whatever you sell, Krevya starts by understanding the product.',
              'Ne satarsan sat, önce ürünü anlar.',
            )}
          </h2>
          <div className="lp-type-cloud" aria-hidden="true">
            {(tr
              ? [
                  'Moda',
                  'Kozmetik',
                  'Ev ve yaşam',
                  'Takviye',
                  'Elektronik',
                  'Dijital ürün',
                  'Ajans',
                  'SaaS',
                ]
              : [
                  'Fashion',
                  'Beauty',
                  'Home',
                  'Supplements',
                  'Electronics',
                  'Digital goods',
                  'Agencies',
                  'SaaS',
                ]
            ).map((type, i) => (
              <span
                style={
                  {
                    '--i': i,
                    // Two rows that frame the heading rather than crowd it,
                    // and a first row low enough to clear the floating bar.
                    '--x': `${4 + (i % 4) * 24}%`,
                    '--y': `${20 + Math.floor(i / 4) * 52}%`,
                  } as CSSProperties
                }
                key={type}
              >
                {type}
              </span>
            ))}
          </div>
        </section>

        <section className="lp-ideas" data-reveal data-scroll="start end / end start">
          <h3>
            {tx('10+ ways to sell the same product', 'Aynı ürünü satmak için 10+ farklı açı.')}
          </h3>
          <div className="lp-marquee">
            <div>
              {[...ideas, ...ideas].map((idea, i) => (
                <span key={`${idea}-${i}`}>{idea}</span>
              ))}
            </div>
          </div>
          <div className="lp-marquee lp-marquee-reverse">
            <div>
              {[...ideas.slice().reverse(), ...ideas.slice().reverse()].map((idea, i) => (
                <span key={`${idea}-${i}`}>{idea}</span>
              ))}
            </div>
          </div>
        </section>

        <section className="lp-intelligence" data-reveal>
          <header>
            <h2>
              {tx(
                'Every creative should have a reason to exist.',
                'Her kreatifin bir nedeni olsun.',
              )}
            </h2>
            <p>
              {tx(
                'Krevya connects each idea to what it found in your product, market and competitor research, so you know what the creative is trying to prove.',
                'Krevya her fikri ürün, pazar veya rakip araştırmasında gördüğü bir sinyale bağlar. Böylece kreatifin neyi kanıtlamaya çalıştığını bilirsin.',
              )}
            </p>
          </header>
          <div className="lp-signal-stage lp-stagger">
            <Mascot pose="search" />
            {signals.map(([title, body], i) => (
              <article className={`lp-signal-${i}`} key={title}>
                <div className="lp-card-head">
                  <b>
                    <LandingIcon name={(['search', 'angles', 'check', 'loop'] as const)[i]!} />
                  </b>
                  <h3>{title}</h3>
                </div>
                <p>{body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="lp-languages lp-lines" data-reveal>
          {/* Turkish keeps the numeral, where "altı dilde" would read as prose
              rather than as a count beside the 40+, 50+, 12+ and 10+ the rest
              of the page states. English spells it, as the rewrite does. */}
          <h2>
            {tx(
              'Six languages. Copy built into the creative.',
              '6 dilde, metni kreatifin içinde hazır.',
            )}
          </h2>
          <p>
            {tx(
              'Generate the visual and its copy together in Turkish, English, German, French, Spanish and Italian.',
              'Görseli ve metnini Türkçe, İngilizce, Almanca, Fransızca, İspanyolca ve İtalyanca birlikte üret.',
            )}
          </p>
          <div className="lp-langs lp-stagger">
            {FLAG_CODES.map((code) => (
              <span key={code}>
                <LandingFlag code={code} />
                {FLAG_LABELS[code]}
              </span>
            ))}
          </div>
        </section>

        <section className="lp-compare lp-sides" data-reveal>
          <header>
            <h2>
              {tx(
                'Stop rebuilding context in three different tools.',
                'Aynı ürünü üç farklı araca tekrar tekrar anlatma.',
              )}
            </h2>
            <p>
              {tx(
                'Krevya keeps research, brand rules, product facts, copy and visuals together from first idea to final asset.',
                'Krevya araştırmayı, marka kurallarını, ürün gerçeklerini, metni ve görseli ilk fikirden son dosyaya kadar aynı yerde tutar.',
              )}
            </p>
          </header>
          <div className="lp-table" role="table">
            {/*
              The vertical mark behind Krevya's column. It is its own grid with
              the table's own template, so the browser works out where that
              column starts rather than a percentage guessing at it, and it is
              one continuous pill down the table instead of five tinted cells
              that happen to line up.
            */}
            <div className="lp-table-ours" aria-hidden="true">
              <i />
            </div>
            {/* Every cell carries its role: a row may only hold cells, and
                without them the logos were loose images inside a row and
                screen readers heard a table with no cells in it. */}
            <div className="lp-table-head" role="row">
              <span role="cell">{tx('What the job needs', 'İşin gerektirdikleri')}</span>
              {comparisons.map((column) => (
                <b
                  key={column.label}
                  role="columnheader"
                  data-brand={column.label === 'Krevya' ? 'us' : undefined}
                >
                  <img
                    className={
                      column.label === 'Krevya' ? 'lp-table-logo' : 'lp-table-logo lp-table-mark'
                    }
                    src={column.logo}
                    alt={column.label}
                    loading="lazy"
                    decoding="async"
                  />
                  {/* The wordmark is too wide for a phone's column; the mark and a name stand in. */}
                  {column.label === 'Krevya' ? (
                    <img
                      className="lp-table-k"
                      src="/brand/mark-96.webp"
                      alt=""
                      width={28}
                      height={28}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : null}
                  <span aria-hidden={column.label === 'Krevya' || undefined}>{column.label}</span>
                </b>
              ))}
            </div>
            {comparisonRows.map((row, i) => (
              <div className="lp-table-row" role="row" key={row} style={{ '--r': i } as CSSProperties}>
                <span role="rowheader">{row}</span>
                {comparisons.map((column) => (
                  <i
                    role="cell"
                    // The tick and the cross are drawn, not written; this is
                    // what they say to someone who cannot see them.
                    aria-label={column.values[i] ? tx('Yes', 'Evet') : tx('No', 'Hayır')}
                    data-yes={column.values[i] || undefined}
                    key={column.label}
                  >
                    <LandingIcon name={column.values[i] ? 'check' : 'cross'} />
                  </i>
                ))}
              </div>
            ))}
          </div>
        </section>

        <section className="lp-privacy lp-lines" data-reveal>
          {/* A padlock that has been checked, drawn here rather than added to
              the icon set: it is the only mark on the page that carries a
              second symbol inside it. */}
          <div className="lp-lock" aria-hidden="true">
            <svg viewBox="0 0 48 54" fill="none" stroke="currentColor" strokeWidth="3">
              <path d="M14 23v-8a10 10 0 0 1 20 0v8" strokeLinecap="round" />
              <rect x="5.5" y="23" width="37" height="27" rx="9" />
              <path d="m18 36.5 4.2 4.2L31 32" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h2>
            {tx('Your store context', 'Mağaza bağlamın')}
            <br />
            {tx('stays ', 'sana ')}
            <span>{tx('yours.', 'ait kalır.')}</span>
          </h2>
          <div className="lp-privacy-cols">
            <p>
              {tx(
                'Product, brand and research data is encrypted in transit and at rest, isolated to your workspace, and deletable on request.',
                'Ürün, marka ve araştırma verilerin aktarımda ve saklamada şifrelenir; yalnızca çalışma alanına açıktır ve talep ettiğinde silinir.',
              )}
            </p>
            {/*
              The one claim on this page that had to be checked against the API
              before it was made. Store Pages does send a page to Shopify, so
              the old "never writes anything back" was false. What is true is
              said here positively: the page is built into the Krevya Theme,
              which `themeCreate` adds to the shop as an unpublished theme (the
              deploy aborts if the target is the live one), and going live is a
              separate act the merchant takes.
            */}
            <p>
              {tx(
                'Store pages are prepared on the Krevya Theme, added to your Shopify store as an unpublished draft. You decide what gets published.',
                'Mağaza sayfaları Krevya Theme’de hazırlanır ve mağazana yayınlanmamış bir taslak olarak eklenir. Neyin yayınlanacağına sen karar verirsin.',
              )}
            </p>
          </div>
        </section>

        <section className="lp-work" data-reveal>
          <h2>
            {tx('Built for people who run the store.', 'Mağazayı gerçekten yönetenler için.')}
            <br />
            <span>{tx('Useful solo. Faster with a team.', 'Tek başına da, ekiple de hızlı.')}</span>
          </h2>
          {/*
            A row rather than a grid of four. The pictures of the work being
            done are the kind of thing there is always one more of, and a grid
            fixes how many the section can ever hold; the row takes as many as
            exist. It is the gallery's own row, which runs by itself and can be
            taken by hand, so the page gains no new behaviour to learn.
          */}
          <GalleryTrack copies={4} className="lp-work-track">
            {[...SLOTS, ...SLOTS, ...SLOTS, ...SLOTS].map((slot, i) => (
              <article className="lp-work-card" key={`${slot}-${i}`}>
                <div>
                  <Mascot pose={POSES[i % POSES.length]!} />
                  <b>{suiteTabs[i % suiteTabs.length]}</b>
                </div>
                <Art slot={`work-${slot}`} />
              </article>
            ))}
          </GalleryTrack>
        </section>

        {/*
          Pricing sits after the proof: by here the visitor has seen what the
          product does. What it does not do yet lives on `/roadmap`, linked
          from the footer.
        */}
        <section
          className="lp-pricing"
          id="pricing"
          data-reveal
          // Every amount stays hidden, in its place, until the server has
          // said which region's currency this visitor is shown.
          data-prices={pricesSettled ? undefined : 'pending'}
        >
          <header>
            <h2>{tx('From research to publishing, one subscription.', 'Araştırmadan yayına, tek abonelik.')}</h2>
            <p>
              {tx(
                'Competitor research, ad creatives, brand memory and store pages in one product. Pick a size; every tool is in every plan.',
                'Rakip araştırması, reklam kreatifleri, marka hafızası ve mağaza sayfaları tek üründe. Boyunu seç; her araç her planda var.',
              )}
            </p>
          </header>
          <div className="lp-pricing-controls">
          {yearly && (
            <div className="lp-pricing-switch" role="group" aria-label={tx('Billing period', 'Ödeme dönemi')}>
              <button type="button" aria-pressed={shown === 'month'} onClick={() => setPeriod('month')}>
                {tx('Monthly', 'Aylık')}
              </button>
              <button type="button" aria-pressed={shown === 'year'} onClick={() => setPeriod('year')}>
                {tx('Yearly', 'Yıllık')}
                {savings !== null && (
                  <small>{tx(`≈${savings}% off`, `≈%${savings} indirim`)}</small>
                )}
              </button>
            </div>
          )}
            {/* The region decides the currency and there is no other to pick:
                this only says so. */}
            <div className="lp-pricing-currency">
              <span>{t('billing.currency.regionNote')}</span>
            </div>
          </div>
          <div className="lp-pricing-grid lp-stagger">
            {plans.map((plan) => {
              const lead = plan.id === 'growth';
              // Arranged by agreement: no checkout, and a conversation where
              // the other cards have a sign-up. It still quotes the year.
              const agreed = !plan.selfServe;
              const yearly = shown === 'year' && plan.yearly !== null;
              const f = planFigures(plan, shown, formatLocale);
              const copy = planCopy[plan.id];
              const note =
                shown === 'year'
                  ? yearly
                    ? tx(`≈ ${f.perMonth ?? ''} a month`, `≈ ayda ${f.perMonth ?? ''}`)
                    : tx('Custom yearly terms', 'Yıllık şartlar görüşmeyle')
                  : lead
                    ? tx(`about ${f.perDay} a day`, `günde yaklaşık ${f.perDay}`)
                    : '';
              return (
                <article
                  key={plan.id}
                  className="lp-plan"
                  data-highlight={lead || undefined}
                  data-agreed={agreed || undefined}
                  data-plan={plan.id}
                >
                  <div className="lp-plan-head">
                    <h3>{plan.name}</h3>
                    {lead && <span className="lp-plan-tag">{t('billing.plans.popular')}</span>}
                  </div>
                  <p className="lp-plan-for">{copy ? tx(copy[0], copy[1]) : ''}</p>
                  <p className="lp-plan-price">
                    <strong>{f.headline}</strong>
                    <span>{yearly ? tx('/ year', '/ yıl') : tx('/ month', '/ ay')}</span>
                  </p>
                  <p className="lp-plan-note">{note}</p>
                  <dl className="lp-plan-facts">
                    <div>
                      <dt>
                        {tx('Credits a month', 'Aylık kredi')}
                      </dt>
                      <dd>{f.credits}</dd>
                    </div>
                    <div>
                      <dt>{tx('Standard images', 'Standart görsel')}</dt>
                      <dd>≈ {f.images}</dd>
                    </div>
                    <div>
                      <dt>{tx('Per credit', 'Kredi başına')}</dt>
                      <dd data-money="">{f.perCredit}</dd>
                    </div>
                    <div>
                      <dt>{tx('Stores', 'Mağaza')}</dt>
                      <dd>
                        {plan.storeLimit === null
                          ? tx('Unlimited', 'Sınırsız')
                          : plan.storeLimit.toLocaleString(formatLocale)}
                      </dd>
                    </div>
                  </dl>
                  {(
                    <ul className="lp-plan-extras">
                      {planPerks(plan.id, agreed).map(([en, trk]) => (
                        <li key={en}>
                          <LandingIcon name="check" />
                          {en === THEME_PERK ? (
                            <span>
                              Krevya Theme <s>{themePrice}</s> {tx('free', 'ücretsiz')}
                            </span>
                          ) : (
                            tx(en, trk)
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {yearly && (
                    <p className="lp-plan-yearcredits">
                      {tx(
                        `${f.yearCredits} over the year, added monthly`,
                        `Yıl boyunca ${f.yearCredits} kredi, her ay yüklenir`,
                      )}
                    </p>
                  )}
                  {agreed ? (
                    <a
                      className="lp-button lp-button-quiet lp-plan-contact"
                      href={partnerContactHref(
                        // Says which price was being read, so the conversation
                        // starts from the year when that is what was shown.
                        yearly
                          ? tx(
                              'Hi, I would like to talk about the Krevya Partner plan, billed yearly.',
                              'Merhaba, Krevya Partner planını yıllık ödemeyle konuşmak istiyorum.',
                            )
                          : tx(
                              'Hi, I would like to talk about the Krevya Partner plan.',
                              'Merhaba, Krevya Partner planı hakkında konuşmak istiyorum.',
                            ),
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <WhatsAppGlyph />
                      {tx('Contact us', 'Bize ulaş')}
                    </a>
                  ) : (
                    <Button quiet={!lead}>{tx('Start', 'Başla')}</Button>
                  )}
                </article>
              );
            })}
          </div>
          {/*
            The theme every store page is built on, priced where the plans are
            priced: it is part of what a subscription buys, so it is read in
            the same breath as the plans rather than as a feature further up.
            Every count on it is taken from `theme/`: sections with a preset
            (the ones a merchant can add in the editor), the block types of the
            product information section, the offer blocks of the product hero,
            the cart drawer's reward bar, the discount popup's three styles and
            the storefront locales. Change the theme, recount these.
          */}
          <ThemeCard
            tx={tx}
            tag={tx('Free with every plan', 'Abonelikle ücretsiz')}
            price={
              <>
                <strong>{money(0, currency, formatLocale)}</strong>
                {/* What the theme costs on its own, struck: the plan gives it free. */}
                <span>
                  <s>{themePrice}</s> {tx('with your plan', 'aboneliğinle')}
                </span>
              </>
            }
            /* The market's price is quoted in dollars, which only a visitor
               priced in dollars is shown: everyone else sees their own
               region's currency and no other. Held hidden with the prices
               until the region is known. */
            value={
              currency === 'usd'
                ? /* The word joiner keeps the range on one line; split at the dash it reads as two prices. */
                  tx(
                    'Premium Shopify themes usually cost $149–⁠$349 one time, for one store.',
                    'Premium Shopify temaları tek mağaza için genelde $149–⁠$349, tek seferlik.',
                  )
                : tx(
                    'Premium Shopify themes are usually a separate one-time purchase, for one store.',
                    'Premium Shopify temaları genelde tek mağaza için ayrıca, tek seferlik satın alınır.',
                  )
            }
            action={<Button>{tx('Start', 'Başla')}</Button>}
          />
          <div className="lp-pricing-tools">
            <strong>{tx('All Krevya tools are included on every plan.', 'Bütün Krevya araçları her planda var.')}</strong>
            <ul>
              {pricingTools.map(([en, trk]) => (
                <li key={en}>
                  <LandingIcon name="check" />
                  {tx(en, trk)}
                </li>
              ))}
            </ul>
          </div>
          <p className="lp-pricing-foot">
            {tx(
              'Payment is handled by Whop. Signing up needs no card; you choose a plan from inside Krevya.',
              'Ödemeyi Whop işler. Kayıt için kart gerekmez; planı Krevya’nın içinden seçersin.',
            )}
          </p>
        </section>

        <section className="lp-faq" id="faq" data-reveal>
          <h2>{tx('Frequently asked', 'Sık sorulanlar')}</h2>
          <div>
            {faqs.map(([q, a], i) => (
              <details key={q} open={i === 0}>
                <summary>
                  {q}
                  <span aria-hidden="true" />
                </summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
        </section>

        {/*
          A person and a number, not a form. A merchant deciding whether to
          hand a store to a new tool wants to know who is behind it, and the
          answer is one person who picks up the phone.
        */}
        <section className="lp-founder" id="contact" data-reveal>
          <img
            src={COMPANY.founder.photo}
            alt={COMPANY.founder.name}
            width={128}
            height={128}
            loading="lazy"
            decoding="async"
          />
          <div>
            <h2>{COMPANY.founder.name}</h2>
            <p className="lp-founder-role">{COMPANY.founder.title[tr ? 'tr' : 'en']}</p>
            <p>
              {tx(
                'A question about Krevya, your store or a plan? Message me on WhatsApp, call, or write to me.',
                'Krevya, mağazan ya da planlar hakkında bir sorun mu var? WhatsApp’tan yaz, ara ya da e-posta gönder.',
              )}
            </p>
            <div className="lp-founder-actions">
              <a
                className="lp-button"
                href={`${COMPANY.whatsappHref}?text=${encodeURIComponent(
                  tx('Hi Umut, I have a question about Krevya.', 'Selam Umut, Krevya hakkında bir sorum var.'),
                )}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <WhatsAppGlyph />
                WhatsApp
              </a>
              <a className="lp-button lp-button-quiet" href={COMPANY.phoneHref}>
                {COMPANY.phone}
              </a>
              <a
                className="lp-button lp-button-quiet"
                href={COMPANY.founder.linkedin}
                target="_blank"
                rel="noopener noreferrer"
              >
                LinkedIn
              </a>
            </div>
            <p className="lp-founder-mail">
              <a href="/about">
                {tx('The founding team and the company', 'Kurucu ekip ve şirket')}
              </a>
              {tx('. ', '. ')}
              {tx('Write to ', 'Yazmak istersen: ')}
              <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
              {tx(', or to me at ', ' ya da doğrudan bana: ')}
              <a href={`mailto:${COMPANY.founderEmail}`}>{COMPANY.founderEmail}</a>
            </p>
          </div>
        </section>

        <section className="lp-final" data-reveal>
          <Mascot pose="heart" />
          <h2>
            {tx(
              'Turn your catalogue into the next campaign.',
              'Kataloğunu bir sonraki kampanyaya dönüştür.',
            )}
          </h2>
          <p>
            {tx(
              'Connect Shopify. Krevya will learn the products, surface the angles, and help you ship the first creative.',
              'Shopify mağazanı bağla. Krevya ürünlerini öğrensin, test edilecek satış açılarını çıkarsın ve ilk kreatifini hazırlasın.',
            )}
          </p>
          {/*
            The closing button is not the hero's button in Turkish.
            Turkish CTAs are verbs, and repeating the hero's verb verbatim ends
            the page on the same word it opened with. The hero asks for the
            chore ("Shopify mağazanı bağla"); the closer starts from the
            merchant's own store.
          */}
          <Button>{tx('Connect Shopify', 'Mağazamla başla')}</Button>
        </section>
      </main>
      <LandingFooter />
      <LandingGuide />
      {offer.open ? <ScratchCard onClose={offer.close} /> : null}
      <StickyOffer tx={tx} suppressed={offer.open} />
    </div>
  );
}

/**
 * The foot of every public page: the landing page and the legal texts.
 *
 * The second row is the part the law asks for. Turkey's e-commerce law wants
 * the seller's title, address and a way to reach it on the site itself, and
 * the KVKK notice has to be one link away from wherever data is collected, so
 * the company and its three texts sit under every page, not on one.
 */
export function LandingFooter() {
  const { locale } = useLocale();
  const t = useT();
  const tr = locale === 'tr';
  const tx = (en: string, turkish: string) => (tr ? turkish : en);
  // The build's year while prerendered HTML is adopted, the visitor's after.
  const year = useRenderNow().getFullYear();
  return (
    <footer className="lp-footer">
      <a className="lp-footer-brand" href="/">
        <img
          src="/brand/krevya-wordmark.webp"
          alt="Krevya"
          width={125}
          height={40}
          loading="lazy"
          decoding="async"
        />
      </a>
      <p>
        {tx('Product-aware creative for ecommerce.', 'Ürünü tanıyan e-ticaret kreatif sistemi.')}
      </p>
      <div>
        <a href="/login">{t('landing.nav.signIn')}</a>
        <a href="/blog">{tx('Blog', 'Blog')}</a>
        <a href="/#how">{t('landing.nav.how')}</a>
        <a href="/#pricing">{t('landing.nav.pricing')}</a>
        <a href="/#faq">{t('landing.nav.faq')}</a>
        <a href="/roadmap">{t('landing.nav.roadmap')}</a>
        <a href="/about">{tx('About', 'Hakkında')}</a>
      </div>
      <div>
        <a href="/legal/privacy">{tx('Privacy', 'Gizlilik ve KVKK')}</a>
        <a href="/legal/terms">{tx('Terms', 'Kullanım koşulları')}</a>
        <a href="/legal/cookies">{tx('Cookies', 'Çerezler')}</a>
        <ConsentLink />
        <a href="/#contact">{tx('Contact', 'İletişim')}</a>
      </div>
      <address className="lp-footer-company">
        <span>
          {tx(
            `Krevya is a product of ${COMPANY.legalName}.`,
            `Krevya, ${COMPANY.legalName} ürünüdür.`,
          )}
        </span>
        <span>{COMPANY.address}</span>
        <a href={COMPANY.phoneHref}>{COMPANY.phone}</a>
        <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
        <small>
          © {year} {COMPANY.legalName}
        </small>
      </address>
    </footer>
  );
}
